
import React, { useEffect, useState } from "react";
import { Share2, Download, Info, X } from "lucide-react";
import { FilePdfOutlined, CalendarOutlined, CaretDownOutlined, CloseCircleOutlined, FilterOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs, Pagination, Badge, DatePicker, Select } from "antd";
import { Backdrop, CircularProgress } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import { Row, Col, Typography, Space } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import SmartVitalsReport from "../SmartVitals/SmartVitalsReport";
import CosmeticPreviousReports from "../SkinCare/CosmeticPreviousReports";
import http from "../../api/Services/HttpCommon";
import { resolvePdfUrl } from "../../api/Services/resolvePdfUrl";
import { getSkincareStatus } from "../SkinCare/SkinCareapi";
import { getLungcareStatus } from "../LungsCare/LungsCareapi";
import { getVisioncareStatus } from "../VisionCare/VisionCareApi";
import WhatsAppShareModal from "../../Shared/WhatsAppShareModal/WhatsAppShareModal";
import lung from "../../Assets/Images/rewamp/sym/report section lungs icon.svg";
import skin from "../../Assets/Images/rewamp/sym/report skin care.svg";
import oral from "../../Assets/Images/rewamp/sym/report -oral care.svg";
import vision from "../../Assets/Images/rewamp/vision care internal screens/vision care report.svg";
import vital from "../../Assets/Images/rewamp/sym/smart vitals report.svg";

// Tabs whose rows are polled one-by-one via a read-only "<service>-status/{ORDERID}" lookup.
// (Skin Care has its own loop and is intentionally left separate.)
const ORDERID_STATUS_APIS = {
    lungs_care: getLungcareStatus,
    vision_care: getVisioncareStatus,
};

const { TabPane } = Tabs;
const { Title, Text } = Typography;

const tabConfig = {
  skin_care: {
    label: "Skin Care",
    icon: skin,
    color: "#ff7875",
    reportName: "AI Skin Report",
  },
  lungs_care: {
    label: "Lung Care",
    icon: lung,
    color: "#40a9ff",
    reportName: "AI Lung Report",
  },
  smart_vitals: {
    label: "Smart Vitals",
    icon: vital,
    color: "#73d13d",
    reportName: "AI Smart Vitals Report",
  },
  oral_care: {
    label: "Oral Care",
    icon: oral,
    color: "#b37feb",
    reportName: "AI Oral Report",
  },
  vision_care: {
    label: "Vision Care",
    icon: vision,
    color: "#b37feb",
    reportName: "AI Vision Report",
  },
};

// ─── Main Component ──────────────────────────────────────────────────────────
export default function AiReports({ onClose = () => {} }) {
    const storedData1 = JSON.parse(localStorage.getItem("loginObj"));
    const userData = storedData1;

    const location = useLocation();
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [skinCareView, setSkinCareView] = useState("normal"); // "normal" | "cosmetic" — only relevant when solutionType === "skin_care"
    const [signedUrl, setSignedUrl] = useState("");
    const [solutionType, setSolutionType] = useState(() => {
        // ?tab= in the URL keeps the selected tab across a page refresh;
        // location.state.tab is how the upload flows open a specific tab
        const urlTab = new URLSearchParams(location?.search).get("tab");
        if (urlTab && tabConfig[urlTab]) return urlTab;
        const requestedTab = location?.state?.tab;
        if (requestedTab && tabConfig[requestedTab]) return requestedTab;
        return "skin_care";
    });
    const [labReportList, setLabReport] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [statusFilter, setStatusFilter] = useState(undefined);
    const [dateRange, setDateRange] = useState([dayjs().subtract(1, "year"), dayjs()]);
    const [smartVitalsReportData, setSmartVitalsReportData] = useState(null);
    const [smartVitalsModalVisible, setSmartVitalsModalVisible] = useState(false);

    // ── Share / Download state ──────────────────────────────────────────────
    const [whatsappModal, setWhatsappModal] = useState(false);
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    // ── Report card extras (Figma) ──────────────────────────────────────────
    const [profileMeta, setProfileMeta] = useState("");   // e.g. "Female, 22" shown after the name
    const [infoOpenId, setInfoOpenId] = useState(null);   // which pending card's "on its way" popover is open

    // Gender + age for the card header come from the patient profile (report rows only carry the name)
    useEffect(() => {
        if (!userData?.code) return undefined;
        let active = true;
        http.get(`patient/${userData.code}`)
            .then((res) => {
                const d = res?.data?.data || res?.data;
                if (!active || !d) return;
                const gender = d.gender === "F" ? "Female" : d.gender === "M" ? "Male" : (d.gender || "");
                // dob comes as MM/DD/YYYY (same as SmartVitals' FormComponent parsing)
                let age = "";
                const m = String(d.dob || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                if (m) {
                    const birth = dayjs(new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2])));
                    if (birth.isValid()) age = String(dayjs().diff(birth, "year"));
                }
                setProfileMeta([gender, age].filter(Boolean).join(", "));
            })
            .catch(() => {});
        return () => { active = false; };
    }, [userData?.code]);

    useEffect(() => {
        const requestedTab = location?.state?.tab;
        if (requestedTab && tabConfig[requestedTab]) setSolutionType(requestedTab);
    }, [location?.state?.tab]);

    useEffect(() => {
        if (location?.state?.scrollTo) window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const buildReportListParams = () => {
        const params = {
            patientid: userData?.code,
            service: tabConfig[solutionType]?.label,
            page_number: currentPage,
            page_size: pageSize,
        };
        if (typeof statusFilter === "number") params.status = statusFilter;
        if (Array.isArray(dateRange) && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
            params.start_date = dateRange[0].format("YYYY-MM-DD");
            params.end_date = dateRange[1].format("YYYY-MM-DD");
        }
        return params;
    };

    const sortByCreatedDesc = (rows) => (rows || []).sort((a, b) => {
        const dateA = new Date(a.CREATEDDATE || a.created_date || 0);
        const dateB = new Date(b.CREATEDDATE || b.created_date || 0);
        return dateB - dateA;
    });

    useEffect(() => {
        if (!userData?.code) return;
        if (solutionType === "skin_care" && skinCareView === "cosmetic") return; // Cosmetic view fetches its own data
        let active = true;
        const fetchReports = async () => {
            setLoading(true);
            try {
                const url = `${process.env.REACT_APP_QUANTUMURL}get_report_list`;
                const response = await http.get(url, { params: buildReportListParams() });
                if (active) {
                    const { data, total_count, page_number } = response.data;
                    setLabReport(sortByCreatedDesc(data));
                    setCurrentPage(page_number || 1);
                    setTotalElements(total_count || 0);
                }
            } catch (error) {
                if (active) { console.error("Error fetching AI reports:", error); setLabReport([]); setTotalElements(0); }
            } finally {
                if (active) setLoading(false);
            }
        };
        fetchReports();
        return () => { active = false; };
    }, [userData?.code, solutionType, skinCareView, currentPage, pageSize, statusFilter, dateRange]);

    // ── Live-refresh pending Skin Care rows ─────────────────────────────────
    // get_report_list is fetched once per page/filter change and doesn't auto-update.
    // For Skin Care specifically, poll the single-request status wrapper for any row
    // still pending so a card flips to "View Report" without a manual refresh.
    // Scoped to this tab only — other services are unaffected.
    useEffect(() => {
        if (solutionType !== "skin_care" || !userData?.code) return undefined;

        const pending = labReportList.filter((r) => {
            const status = Number(r?.STATUS ?? r?.status);
            const pdfUrl = r?.PDFREPORT ?? r?.pdf_report;
            const requestId = r?.ORDERID ?? r?.REPORTID ?? r?.REQUESTID;
            return requestId && status !== 2 && !pdfUrl;
        });
        if (!pending.length) return undefined;

        const POLL_MS = 9000;
        const MAX_ATTEMPTS = 20; // ~3 min of polling before backing off until the list itself refreshes
        let cancelled = false;
        let attempts = 0;

        const poll = async () => {
            if (cancelled) return;
            if (document.hidden) return; // skip this tick while the tab is backgrounded — no point polling nobody's watching
            attempts += 1;
            if (attempts > MAX_ATTEMPTS) { clearInterval(id); return; }

            const updates = await Promise.all(
                pending.map(async (row) => {
                    const requestId = row?.ORDERID ?? row?.REPORTID ?? row?.REQUESTID;
                    try {
                        const res = await getSkincareStatus(requestId, userData.code);
                        const data = res.data;
                        const status = Number(data?.STATUS);
                        if (data && (status === 1 || status === 2)) {
                            return { id: row?.ID ?? row?.id, data };
                        }
                    } catch (err) {
                        console.error("[AiReports] skincare-status poll failed:", err);
                    }
                    return null;
                })
            );
            if (cancelled) return;
            const resolved = updates.filter(Boolean);
            if (resolved.length) {
                setLabReport((prev) =>
                    prev.map((r) => {
                        const match = resolved.find((u) => u.id === (r?.ID ?? r?.id));
                        return match ? { ...r, ...match.data } : r;
                    })
                );
            }
        };

        const id = setInterval(poll, POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, [solutionType, labReportList, userData?.code]);

    // ── Live-refresh pending Lung Care / Vision Care rows ──────────────────
    // Same idea as Skin Care above, via the read-only <service>-status lookup.
    // These rows have no ID column, so rows are matched on ORDERID.
    useEffect(() => {
        const getStatus = ORDERID_STATUS_APIS[solutionType];
        if (!getStatus || !userData?.code) return undefined;

        const pending = labReportList.filter((r) => {
            const status = Number(r?.STATUS ?? r?.status);
            const pdfUrl = r?.PDFREPORT ?? r?.pdf_report;
            return r?.ORDERID && status !== 2 && !pdfUrl;
        });
        if (!pending.length) return undefined;

        const POLL_MS = 9000;
        const MAX_ATTEMPTS = 45; // ~7 min — the backend gives the lung model up to 300s, plus queue time
        let cancelled = false;
        let attempts = 0;

        const poll = async () => {
            if (cancelled) return;
            if (document.hidden) return; // skip this tick while the tab is backgrounded
            attempts += 1;
            if (attempts > MAX_ATTEMPTS) { clearInterval(id); return; }

            const updates = await Promise.all(
                pending.map(async (row) => {
                    try {
                        const res = await getStatus(row.ORDERID, userData.code);
                        const data = res.data;
                        const status = Number(data?.STATUS);
                        if (data && (status === 1 || status === 2)) {
                            return { orderId: row.ORDERID, data };
                        }
                    } catch (err) {
                        console.error(`[AiReports] ${solutionType} status poll failed:`, err);
                    }
                    return null;
                })
            );
            if (cancelled) return;
            const resolved = updates.filter(Boolean);
            if (resolved.length) {
                setLabReport((prev) =>
                    prev.map((r) => {
                        const match = resolved.find((u) => u.orderId === r?.ORDERID);
                        return match ? { ...r, ...match.data } : r;
                    })
                );
            }
        };

        const id = setInterval(poll, POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, [solutionType, labReportList, userData?.code]);

    // ── Live-refresh pending rows for the other services ─────────────────────
    // While any row on the current page is still pending, silently re-fetch the
    // list in the background (no loader) so it flips to "View Report" on its own.
    // Skin Care, Lung Care and Vision Care are skipped here — they have their own per-request polls above.
    useEffect(() => {
        if (!userData?.code || solutionType === "skin_care" || ORDERID_STATUS_APIS[solutionType]) return undefined;

        const hasPending = labReportList.some((r) => getReportState(r).label === "Report Pending");
        if (!hasPending) return undefined;

        const POLL_MS = 10000;
        const MAX_ATTEMPTS = 60; // ~10 min; after that the user can switch tab/page to re-check
        let cancelled = false;
        let attempts = 0;
        let timer;

        const rowKey = (r) => `${r?.ID ?? r?.id}|${r?.STATUS ?? r?.status}|${r?.PDFREPORT ?? r?.pdf_report ?? ""}`;

        const poll = async () => {
            if (cancelled) return;
            if (!document.hidden) {
                attempts += 1;
                try {
                    const url = `${process.env.REACT_APP_QUANTUMURL}get_report_list`;
                    const response = await http.get(url, { params: buildReportListParams() });
                    if (cancelled) return;
                    const { data, total_count } = response.data || {};
                    const next = sortByCreatedDesc(data);
                    // Only update state when something actually changed, so we don't re-render every tick
                    const changed = next.length !== labReportList.length
                        || next.some((r, i) => rowKey(r) !== rowKey(labReportList[i]));
                    if (changed) {
                        setLabReport(next);
                        setTotalElements(total_count || 0);
                        return; // the effect re-runs with the new list and decides whether to keep polling
                    }
                } catch (err) {
                    console.error("[AiReports] pending-report poll failed:", err);
                }
            }
            if (!cancelled && attempts < MAX_ATTEMPTS) timer = setTimeout(poll, POLL_MS);
        };

        // Check right away when the user comes back to this browser tab
        const onVisible = () => {
            if (document.hidden || cancelled) return;
            clearTimeout(timer);
            poll();
        };
        document.addEventListener("visibilitychange", onVisible);

        timer = setTimeout(poll, POLL_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
            document.removeEventListener("visibilitychange", onVisible);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solutionType, labReportList, userData?.code, currentPage, pageSize, statusFilter, dateRange]);

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleTabChange = (key) => {
        setSolutionType(key);
        setCurrentPage(1);
        setSkinCareView("normal");
        // Remember the tab in the URL (replace, so tab clicks don't pile up in browser history)
        navigate({ pathname: location.pathname, search: `?tab=${key}` }, { replace: true });
    };

    const handlePreview = async (filename) => {
        setModalVisible(true);
        setModalLoading(true);
        try {
            if (!filename || typeof filename !== "string") { toast.error("Invalid report filename"); setModalLoading(false); return; }
            const resolvedUrl = await resolvePdfUrl(filename);
            if (!resolvedUrl) { toast.error("Could not prepare preview"); setSignedUrl(""); setModalLoading(false); return; }
            setSignedUrl(`${resolvedUrl}#toolbar=0&navpanes=0&scrollbar=0`);
            setModalLoading(false);
        } catch {
            toast.error("Could not prepare preview");
            setSignedUrl("");
            setModalLoading(false);
        }
    };

    // ── Download handler ────────────────────────────────────────────────────
    const handleDownload = async (pdfUrl, reportId) => {
        if (!pdfUrl) { toast.error("PDF not available"); return; }
        try {
            const resolvedUrl = await resolvePdfUrl(pdfUrl);
            if (!resolvedUrl) { toast.error("PDF not available"); return; }
            const link = document.createElement("a");
            link.href = resolvedUrl;
            link.download = `${tabConfig[solutionType]?.reportName?.replace(/\s+/g, "_") || "AI_Report"}_${reportId || Date.now()}.pdf`;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch {
            toast.error("Error downloading PDF");
        }
    };

    // ── WhatsApp share handler ──────────────────────────────────────────────
    const handleShareClick = (report) => { setSelectedReport(report); setWhatsappModal(true); };

    const handleWhatsAppShare = async (mobileNumber) => {
        if (!selectedReport) { toast.error("No report selected"); return; }
        setWhatsappLoading(true);
        try {
            const rawPdfUrl = selectedReport?.PDFREPORT ?? selectedReport?.pdf_report;
            const resolvedPdfUrl = await resolvePdfUrl(rawPdfUrl);
            if (!resolvedPdfUrl) { toast.error("PDF not available"); setWhatsappLoading(false); return; }
            const reportId = selectedReport?.ID ?? selectedReport?.id;
            const patientName = selectedReport?.PATIENTNAME
                || `${userData?.salutation || ""}${userData?.firstName || ""} ${userData?.lastName || ""}`.trim();

            const params = new URLSearchParams({
                patientid: userData?.code,
                orderid: reportId,
                patient_name: patientName,
                pdf_url: resolvedPdfUrl,
                mobile: mobileNumber,
                service: tabConfig[solutionType]?.label,
            });
            const response = await fetch(
                `${process.env.REACT_APP_QUANTUMURL}get_pdf_whatsapp?${params.toString()}`,
                { method: "GET" }
            );
            const responseData = await response.json();
            // Use whatsapp_status === 200 as the reliable success indicator
            // (backend status field has a known typo: "succeupdate_upload_status_servicess")
            const isSuccess =
                responseData.whatsapp_status === 200 ||
                String(responseData.message).toLowerCase().includes("sent") ||
                String(responseData.status).toLowerCase().includes("success");
            if (isSuccess) {
                toast.success("Report successfully sent to WhatsApp! ✅");
                setWhatsappModal(false);
                setSelectedReport(null);
            } else {
                toast.error(responseData.message || "Failed to send report");
            }
        } catch {
            toast.error("Error sending report. Please try again.");
        } finally {
            setWhatsappLoading(false);
        }
    };

    const formatFriendlyDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        try {
            const parts = new Intl.DateTimeFormat("en-IN", {
                timeZone: "Asia/Kolkata", day: "2-digit", month: "long",
                hour: "2-digit", minute: "2-digit", hour12: true,
            }).formatToParts(d);
            const get = (t) => (parts.find((p) => p.type === t) || {}).value || "";
            return `${parseInt(get("day"), 10)} ${get("month")} at ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
        } catch {
            return new Date(value).toLocaleString("en-IN");
        }
    };

    const getReportState = (report) => {
        const rawStatus = report?.STATUS ?? report?.status;
        const status = Number(rawStatus);
        const pdfUrl = report?.PDFREPORT ?? report?.pdf_report;
        if (status === 2) return { label: "Report Failed", disabled: true, pdfUrl };
        if (solutionType === "smart_vitals") {
            if (status === 1) return { label: "View Report", disabled: false, pdfUrl };
            return { label: "Report Pending", disabled: true, pdfUrl };
        }
        if (status === 0 || !pdfUrl) return { label: "Report Pending", disabled: true, pdfUrl };
        return { label: "View Report", disabled: false, pdfUrl };
    };

    const customEmptyComponent = (
        <div style={{ textAlign: "center", padding: "60px 20px", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)", borderRadius: "16px", margin: "20px 0" }}>
            <div style={{ fontSize: "64px", marginBottom: "16px" }}>📄</div>
            <Title level={4} style={{ color: "#8c8c8c", marginBottom: "8px" }}>No Reports Found</Title>
            <Text style={{ color: "#bfbfbf", fontSize: "16px" }}>No reports available for {tabConfig[solutionType]?.label}</Text>
        </div>
    );

    return (
        <div
            className="min-h-screen"
            style={{
                background: "var(--section-bg-gradient), #fff",
                fontFamily: "Inter, sans-serif",
            }}
        >
            <ToastContainer position="top-right" />

            <Backdrop
                sx={{ color: "#5579C6", zIndex: (theme) => theme.zIndex.drawer + 1, backdropFilter: "blur(4px)" }}
                open={loading}
            >
                <div style={{ textAlign: "center" }}>
                    <CircularProgress color="inherit" size={60} />
                    <div style={{ marginTop: 16, color: "#5579C6", fontSize: "16px" }}>Loading reports...</div>
                </div>
            </Backdrop>

            <div className="px-4 md:px-8 xl:px-[max(32px,calc(50%_-_688px))] mt-5">
                {/* Header */}
                <div className="w-full rounded-xl md:px-10 px-5 md:py-7 py-4 mb-4 md:mb-5" style={{ background: "#F2F8FD" }}>
                    <div className="md:flex justify-between items-center">
                        <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: isMobile ? "22px" : "30px", lineHeight: "100%", letterSpacing: "0.3%", verticalAlign: "middle", color: "var(--color-brand-primary)", display: "inline-block" }}>
                            My AI Reports
                        </span>
                    </div>
                </div>

                {/* Tabs + Filters */}
                <div className="flex-col md:flex-row flex align-items-center justify-between" style={{ gap: 16, marginBottom: 24 }}>
                    <div style={{ flex: isMobile ? "0 0 100%" : "0 0 65%" }}>
                        <style>{`
                          .ai-reports-tabs .ant-tabs-nav { width: 100%; }
                          .ai-reports-tabs .ant-tabs-nav-list { display: flex; width: 100%; }
                          .ai-reports-tabs .ant-tabs-tab { flex: 1 1 0; justify-content: center; border-radius: 0 !important; border-top: none !important; border-left: none !important; border-right: none !important; border-bottom: 2px solid transparent !important; background: transparent !important; font-family: Inter, sans-serif; font-weight: 600; font-size: 15px; color: #2B2B2B !important; }
                          .ai-reports-tabs .ant-tabs-tab + .ant-tabs-tab { border-left: 1px solid #E5E7EB !important; }
                          .ai-reports-tabs .ant-tabs-tab:hover { color: var(--color-brand-primary) !important; }
                          .ai-reports-tabs .ant-tabs-tab.ant-tabs-tab-active { border-bottom: 2px solid var(--color-brand-primary) !important; background: transparent !important; }
                          .ai-reports-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn { font-family: Inter, sans-serif; font-weight: 600 !important; font-size: 14px; letter-spacing: 0.3%; color: var(--color-brand-primary) !important; }
                          .ai-reports-tabs .ant-tabs-ink-bar { display: none !important; }
                          .ai-reports-tabs .ant-tabs-nav::before { border-bottom: 1px solid #E5E7EB !important; }
                          @media (max-width: 768px) {
                            .ai-reports-tabs .ant-tabs-nav { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
                            .ai-reports-tabs .ant-tabs-nav-list { display: inline-flex; width: auto; gap: 8px; }
                            .ai-reports-tabs .ant-tabs-tab { flex: 0 0 auto; white-space: nowrap; padding: 6px 10px; }
                          }
                          .ai-reports-pagination .ant-pagination-item { display: inline-flex; align-items: center; justify-content: center; }
                          .ai-reports-pagination .ant-pagination-item a { line-height: 32px; transform: translateY(-8px); }
                          .ai-reports-pagination .ant-pagination-item-active { background: transparent !important; border: 1px solid transparent !important; display: inline-flex; align-items: center; justify-content: center; }
                          .ai-reports-pagination .ant-pagination-item-active a { color: var(--color-brand-primary) !important; font-weight: 600 !important; line-height: 32px; }
                          .ai-reports-pagination .ant-pagination-item { border-radius: 6px !important; }
                          .ai-reports-pagination .ant-pagination-item:hover { border-color: var(--color-brand-primary) !important; }
                          .ai-reports-pagination .ant-pagination-item:hover a { color: var(--color-brand-primary) !important; }
                          .ai-reports-pagination .ant-pagination-prev:hover .ant-pagination-item-link,
                          .ai-reports-pagination .ant-pagination-next:hover .ant-pagination-item-link { color: var(--color-brand-primary) !important; border-color: var(--color-brand-primary) !important; }
                        `}</style>
                        <Tabs
                            activeKey={solutionType}
                            onChange={handleTabChange}
                            size={isMobile ? "small" : "large"}
                            type="line"
                            tabBarGutter={0}
                            className="ai-reports-tabs"
                            style={{ background: "transparent", padding: 0, boxShadow: "none" }}
                        >
                            {Object.entries(tabConfig).map(([key, config]) => (
                                <TabPane
                                    key={key}
                                    tab={
                                        <Space>
                                            <span>{config.label}</span>
                                            <Badge count={key === solutionType ? totalElements : 0} style={{ backgroundColor: config.color }} />
                                        </Space>
                                    }
                                />
                            ))}
                        </Tabs>
                    </div>

                    {/* Date + Status filters — not applicable to the Cosmetic Skincare view, which has its own filters */}
                    {!(solutionType === "skin_care" && skinCareView === "cosmetic") && (
                    <div style={{ flex: "0 0 30%" }}>
                        <div className="md:flex flex justify-between md:justify-end items-center gap-3 mt-2">
                            <Space>
                                <div style={{ position: "relative", display: "inline-block" }}>
                                    <div className="min-w-[200px] md:min-w-[260px]" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 44, padding: "0 16px", background: "#fff", border: "1px solid var(--color-brand-primary)", borderRadius: "50px", cursor: "pointer", transition: "all 0.3s" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <CalendarOutlined style={{ color: "var(--color-brand-primary)", fontSize: "16px" }} />
                                            <span style={{ color: dateRange?.length ? "var(--color-brand-primary)" : "#9CA3AF", fontSize: "14px", fontWeight: 500 }}>
                                                {dateRange?.length === 2
                                                    ? (isMobile ? `${dateRange[0].format("DD MMM")} - ${dateRange[1].format("DD MMM")}` : `${dateRange[0].format("DD MMM 'YY")} - ${dateRange[1].format("DD MMM 'YY")}`)
                                                    : "Date"}
                                            </span>
                                        </div>
                                        {dateRange?.length === 2 ? (
                                            <CloseCircleOutlined style={{ color: "var(--color-text-subheading)", fontSize: "14px", zIndex: 10, position: "relative" }} onClick={(e) => { e.stopPropagation(); setDateRange([]); }} />
                                        ) : (
                                            <CaretDownOutlined style={{ color: "var(--color-text-subheading)", fontSize: "12px" }} />
                                        )}
                                    </div>
                                    <DatePicker.RangePicker
                                        onChange={(vals) => setDateRange(vals || [])}
                                        disabledDate={(current) => current && current > dayjs().endOf("day")}
                                        value={dateRange?.length === 2 ? dateRange : null}
                                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", zIndex: 1 }}
                                    />
                                </div>
                            </Space>
                            <Space>
                                <div style={{ position: "relative", display: "inline-block" }}>
                                    <div className="min-w-[130px] md:min-w-[160px]" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 44, padding: "0 16px", background: "#fff", border: "1px solid var(--color-brand-primary)", borderRadius: "50px", cursor: "pointer", transition: "all 0.3s" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <FilterOutlined style={{ color: "var(--color-brand-primary)", fontSize: "16px" }} />
                                            <span style={{ color: "var(--color-brand-primary)", fontSize: "14px", fontWeight: 500 }}>
                                                {statusFilter === undefined ? "All" : statusFilter === 1 ? "Completed" : statusFilter === 0 ? "Pending" : "Failed"}
                                            </span>
                                        </div>
                                        <CaretDownOutlined style={{ color: "var(--color-text-subheading)", fontSize: "12px" }} />
                                    </div>
                                    <Select
                                        placeholder="Status"
                                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", zIndex: 1 }}
                                        value={statusFilter === undefined ? "all" : statusFilter}
                                        onChange={(val) => setStatusFilter(val === "all" ? undefined : Number(val))}
                                        options={[
                                            { value: "all", label: "All" },
                                            { value: 1, label: "Completed" },
                                            { value: 0, label: "Pending" },
                                            { value: 2, label: "Failed" },
                                        ]}
                                    />
                                </div>
                            </Space>
                        </div>
                    </div>
                    )}
                </div>

                {/* Normal Skincare / Cosmetic Skincare toggle — Skin Care tab only */}
                {solutionType === "skin_care" && (
                    <div style={{ marginBottom: 20, display: "flex", gap: 10 }}>
                        <button
                            onClick={() => setSkinCareView("normal")}
                            style={{
                                padding: "9px 22px",
                                borderRadius: 999,
                                fontSize: 13,
                                fontWeight: 600,
                                fontFamily: "Inter, sans-serif",
                                cursor: "pointer",
                                border: skinCareView === "normal" ? "1.5px solid var(--color-brand-primary)" : "1px solid #d9d9d9",
                                background: skinCareView === "normal" ? "linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)" : "#fff",
                                color: skinCareView === "normal" ? "var(--color-brand-primary)" : "#555",
                            }}
                        >
                            Normal Skincare
                        </button>
                        <button
                            onClick={() => setSkinCareView("cosmetic")}
                            style={{
                                padding: "9px 22px",
                                borderRadius: 999,
                                fontSize: 13,
                                fontWeight: 600,
                                fontFamily: "Inter, sans-serif",
                                cursor: "pointer",
                                border: skinCareView === "cosmetic" ? "1.5px solid var(--color-brand-primary)" : "1px solid #d9d9d9",
                                background: skinCareView === "cosmetic" ? "linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)" : "#fff",
                                color: skinCareView === "cosmetic" ? "var(--color-brand-primary)" : "#555",
                            }}
                        >
                            Cosmetic Skincare
                        </button>
                    </div>
                )}

                {/* Cosmetic Skincare view — same UI as "View Past Reports" on the cosmetic analysis flow */}
                {solutionType === "skin_care" && skinCareView === "cosmetic" ? (
                    <CosmeticPreviousReports />
                ) : (
                <>
                {/* Reports Grid */}
                <div className="pb-4" style={{ minHeight: "400px" }}>
                    {labReportList.length > 0 ? (
                        <>
                            <Row gutter={[20, 20]}>
                                {labReportList.map((report) => {
                                    const reportState = getReportState(report);
                                    const reportId = report?.ID ?? report?.id;
                                    return (
                                        <Col key={reportId} xs={24} xl={12}>
                                            {(() => {
                                                const rawName = report?.PATIENTNAME || `${userData?.firstName || ""} ${userData?.lastName || ""}`;
                                                const displayName = rawName.replace(/^(mr|mrs|ms|miss|dr|master)\.?\s+/i, "").trim();
                                                const nameParts = displayName.split(/\s+/).filter(Boolean);
                                                const initials = `${nameParts[0]?.[0] || ""}${nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : ""}`.toUpperCase();
                                                const isPending = reportState.label === "Report Pending";
                                                const isFailed = reportState.label === "Report Failed";
                                                const isReady = !reportState.disabled;
                                                const cardKey = report?.ORDERID ?? reportId;
                                                const iconBtn = (enabled) => ({
                                                    width: isMobile ? 36 : 44, height: isMobile ? 36 : 44, borderRadius: "50%",
                                                    border: "1.5px solid var(--color-brand-primary)", background: "#fff",
                                                    color: "var(--color-brand-primary)",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    cursor: enabled ? "pointer" : "not-allowed", opacity: enabled ? 1 : 0.35,
                                                    flexShrink: 0, padding: 0,
                                                });
                                                const statusStyle = isReady
                                                    ? { background: "var(--color-brand-primary)", color: "#fff", cursor: "pointer" }
                                                    : isFailed
                                                        ? { background: "#FDECEC", color: "#C0392B", cursor: "default" }
                                                        : { background: "#EBEBEB", color: "#F28C28", cursor: "default" };
                                                return (
                                                    <div style={{
                                                        height: "100%", background: "#fff", borderRadius: 14,
                                                        border: "1px solid #E6EEF5", boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                                                        padding: isMobile ? "16px" : "24px 26px", fontFamily: "Inter, sans-serif",
                                                    }}>
                                                        {/* Header: avatar · name/meta/ID · share + download */}
                                                        <div style={{ display: "flex", alignItems: "flex-start", gap: isMobile ? 12 : 20 }}>
                                                            <div style={{
                                                                width: isMobile ? 56 : 84, height: isMobile ? 56 : 84, borderRadius: "50%", flexShrink: 0,
                                                                background: "#DCEBFA", color: "#111", fontWeight: 600,
                                                                fontSize: isMobile ? 20 : 30, display: "flex", alignItems: "center", justifyContent: "center",
                                                            }}>
                                                                {initials}
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0, paddingTop: isMobile ? 4 : 12 }}>
                                                                <div style={{ lineHeight: 1.3, wordBreak: "break-word" }}>
                                                                    <span style={{ fontSize: isMobile ? 17 : 22, fontWeight: 700, color: "#2B2B2B" }}>{displayName}</span>
                                                                    {profileMeta && (
                                                                        <span style={{ fontSize: isMobile ? 13 : 16, fontWeight: 500, color: "#6B7280" }}>, {profileMeta}</span>
                                                                    )}
                                                                </div>
                                                                <p style={{ margin: "6px 0 0", fontSize: isMobile ? 13 : 15, color: "#222" }}>
                                                                    Patient ID. {userData?.code}
                                                                </p>
                                                            </div>
                                                            <div style={{ display: "flex", gap: isMobile ? 8 : 10 }}>
                                                                <button
                                                                    type="button"
                                                                    title={isReady ? "Share on WhatsApp" : "Available once the report is ready"}
                                                                    aria-label="Share report"
                                                                    disabled={!isReady}
                                                                    onClick={() => handleShareClick(report)}
                                                                    style={iconBtn(isReady)}
                                                                >
                                                                    <Share2 size={isMobile ? 16 : 19} strokeWidth={2} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title={isReady ? "Download PDF" : "Available once the report is ready"}
                                                                    aria-label="Download report"
                                                                    disabled={!isReady}
                                                                    onClick={() => handleDownload(reportState.pdfUrl, reportId)}
                                                                    style={iconBtn(isReady)}
                                                                >
                                                                    <Download size={isMobile ? 16 : 19} strokeWidth={2} />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Footer: report pill · status · info */}
                                                        <div style={{
                                                            display: "flex", alignItems: "center", gap: 10,
                                                            marginTop: isMobile ? 14 : 22, flexWrap: isMobile ? "wrap" : "nowrap",
                                                        }}>
                                                            <div style={{
                                                                flex: isMobile ? "1 1 100%" : 1, minWidth: 0,
                                                                display: "flex", alignItems: "center", gap: 10,
                                                                background: "#F1F8FD", borderRadius: 8, padding: isMobile ? "9px 12px" : "11px 14px",
                                                            }}>
                                                                <img src={tabConfig?.[solutionType]?.icon} alt="" style={{ width: 24, height: 24, flexShrink: 0 }} />
                                                                <span style={{ fontSize: isMobile ? 12.5 : 14, color: "#2B2B2B", lineHeight: 1.4 }}>
                                                                    <b style={{ color: "#1F2937" }}>{tabConfig?.[solutionType]?.reportName}</b>
                                                                    {"  generated on "}
                                                                    <b style={{ color: "#111" }}>{formatFriendlyDate(report?.CREATEDDATE || report?.created_date)}</b>
                                                                </span>
                                                            </div>

                                                            <button
                                                                type="button"
                                                                disabled={!isReady}
                                                                onClick={() => {
                                                                    if (solutionType === "smart_vitals") {
                                                                        setSmartVitalsReportData(report);
                                                                        setSmartVitalsModalVisible(true);
                                                                    } else {
                                                                        handlePreview(reportState.pdfUrl);
                                                                    }
                                                                }}
                                                                style={{
                                                                    ...statusStyle, border: "none", borderRadius: 8,
                                                                    padding: isMobile ? "9px 14px" : "11px 18px",
                                                                    fontSize: isMobile ? 13 : 15, fontWeight: 600, whiteSpace: "nowrap",
                                                                    flex: isMobile ? 1 : "none",
                                                                }}
                                                            >
                                                                {reportState.label}
                                                            </button>

                                                            {isPending && (
                                                                <div style={{ position: "relative", flexShrink: 0 }}>
                                                                    <button
                                                                        type="button"
                                                                        aria-label="Why is my report pending?"
                                                                        onClick={() => setInfoOpenId(infoOpenId === cardKey ? null : cardKey)}
                                                                        style={{
                                                                            width: 40, height: 40, borderRadius: "50%", border: "1px solid #C9EEF8",
                                                                            background: "#DFF1FB", color: "var(--color-brand-primary)",
                                                                            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
                                                                        }}
                                                                    >
                                                                        <Info size={18} strokeWidth={2} />
                                                                    </button>

                                                                    {infoOpenId === cardKey && (
                                                                        <div style={{
                                                                            position: "absolute", top: "calc(100% + 12px)", right: -8, zIndex: 30,
                                                                            width: isMobile ? "min(300px, 84vw)" : 360,
                                                                            background: "#fff", borderRadius: 14, border: "1px solid #E6EEF5",
                                                                            boxShadow: "0 10px 28px rgba(0,0,0,0.14)", padding: 16,
                                                                            display: "flex", gap: 12, alignItems: "flex-start",
                                                                        }}>
                                                                            {/* pointer arrow under the info button */}
                                                                            <span style={{
                                                                                position: "absolute", top: -7, right: 22, width: 12, height: 12,
                                                                                background: "#fff", borderLeft: "1px solid #E6EEF5", borderTop: "1px solid #E6EEF5",
                                                                                transform: "rotate(45deg)",
                                                                            }} />
                                                                            <div style={{
                                                                                width: 40, height: 40, borderRadius: 10, background: "#E4F7EC", flexShrink: 0,
                                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                            }}>
                                                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
                                                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                                                                                </svg>
                                                                            </div>
                                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                                <p style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, color: "var(--color-brand-primary)" }}>
                                                                                    Your report is on its way
                                                                                </p>
                                                                                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "#4B5563" }}>
                                                                                    It can take a little while to prepare. We'll send it to you on WhatsApp as soon as it's ready, and it will appear here automatically.
                                                                                </p>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                aria-label="Close"
                                                                                onClick={() => setInfoOpenId(null)}
                                                                                style={{
                                                                                    width: 28, height: 28, borderRadius: "50%", border: "none", background: "#EEF2F6",
                                                                                    color: "#4B5563", display: "flex", alignItems: "center", justifyContent: "center",
                                                                                    cursor: "pointer", flexShrink: 0, padding: 0,
                                                                                }}
                                                                            >
                                                                                <X size={14} strokeWidth={2.4} />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </Col>
                                    );
                                })}
                            </Row>

                            {totalElements > 0 && (
                                <div style={{ display: "flex", justifyContent: "center", marginTop: "40px", padding: "24px", background: "white", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                                    <Pagination
                                        className="ai-reports-pagination"
                                        current={currentPage}
                                        pageSize={pageSize}
                                        total={totalElements}
                                        onChange={handlePageChange}
                                        showSizeChanger={false}
                                        showQuickJumper={false}
                                        showTotal={(total, range) => (
                                            <Text style={{ color: "#666", fontSize: "14px" }}>
                                                Showing {range[0]}-{range[1]} of {total} reports
                                            </Text>
                                        )}
                                        size={isMobile ? "small" : "default"}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        !loading && customEmptyComponent
                    )}
                </div>
                </>
                )}
            </div>

            {/* PDF Preview Modal */}
            <Dialog
                header={
                    <div style={{ display: "flex", alignItems: "center", padding: "8px 0" }}>
                        <FilePdfOutlined style={{ fontSize: "20px", color: "#e93f33", marginRight: "12px" }} />
                        <span style={{ fontSize: "18px", fontWeight: 600 }}>Report Preview</span>
                    </div>
                }
                visible={modalVisible}
                style={{ width: isMobile ? "95vw" : "70vw", height: isMobile ? "90vh" : "85vh", maxWidth: "1200px" }}
                onHide={() => { setSignedUrl(""); setModalVisible(false); }}
                blockScroll
                className="report-modal"
            >
                {modalLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                        <CircularProgress />
                    </div>
                ) : signedUrl ? (
                    <iframe title="Report Preview" src={signedUrl} style={{ width: "100%", height: "100%", border: "none", borderRadius: "8px" }} />
                ) : (
                    <div style={{ textAlign: "center", marginTop: "20px", fontSize: "16px", color: "#666" }}>No preview available.</div>
                )}
            </Dialog>

            {/* Smart Vitals Modal */}
            <Dialog
                header="Smart Vitals Report"
                visible={smartVitalsModalVisible}
                style={{ width: isMobile ? "95vw" : "90vw", height: isMobile ? "90vh" : "90vh", maxWidth: "1400px" }}
                onHide={() => { setSmartVitalsReportData(null); setSmartVitalsModalVisible(false); }}
            >
                {smartVitalsReportData && <SmartVitalsReport vitalsData={smartVitalsReportData} isModal={true} />}
            </Dialog>

            {/* WhatsApp Share Modal */}
            <WhatsAppShareModal
                isOpen={whatsappModal}
                onClose={() => { setWhatsappModal(false); setSelectedReport(null); }}
                onConfirm={handleWhatsAppShare}
                loading={whatsappLoading}
            />

            <style jsx>{`
                .report-modal .p-dialog-content { padding: 0 !important; }
                .report-modal .p-dialog-header { border-bottom: 1px solid #f0f0f0; padding: 16px 24px; }
                .ant-tabs-card > .ant-tabs-content { margin-top: 0; }
                .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane { background: transparent; }
                .ant-select-selector { border-radius: 6px !important; }
            `}</style>
        </div>
    );
}